import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { brandsAPI, contentAPI } from '../utils/api';
import { Image, Video } from 'lucide-react';
import { toast } from 'sonner';

const MediaLibrary = () => {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      fetchMedia();
    }
  }, [selectedBrand]);

  const fetchBrands = async () => {
    try {
      const response = await brandsAPI.getAll();
      setBrands(response.data.brands);
      if (response.data.brands.length > 0) {
        setSelectedBrand(response.data.brands[0].id);
      }
    } catch (error) {
      toast.error('Failed to fetch brands');
    }
  };

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const response = await contentAPI.getMedia(selectedBrand);
      setMedia(response.data.media);
    } catch (error) {
      toast.error('Failed to fetch media');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold heading">Media Library</h1>
            <p className="text-gray-600 mt-2">AI-generated images and videos</p>
          </div>
          <div className="w-64">
            <Select value={selectedBrand} onValueChange={setSelectedBrand}>
              <SelectTrigger>
                <SelectValue placeholder="Select brand" />
              </SelectTrigger>
              <SelectContent>
                {brands.map((brand) => (
                  <SelectItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : media.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {media.map((item) => (
              <Card key={item.id} className="card overflow-hidden group">
                <div className="aspect-square bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center relative">
                  {item.content_type === 'image' ? (
                    <Image className="w-16 h-16 text-blue-600" />
                  ) : (
                    <Video className="w-16 h-16 text-purple-600" />
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center">
                    <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">
                      {item.content_type === 'image' ? 'View Image' : 'Play Video'}
                    </span>
                  </div>
                </div>
                <CardContent className="p-4">
                  <p className="text-sm font-medium truncate">{item.title || `${item.content_type} - ${item.id.slice(0, 8)}`}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(item.created_at).toLocaleDateString()}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card text-center py-16">
            <CardContent>
              <p className="text-gray-500">No media available yet. AI will generate images and videos weekly!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MediaLibrary;
